import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Search, Plus, MoreVertical, Users, FolderKanban,
  Calendar, Activity, Shield, Trash2, Pause, Play, Edit, Eye,
  ChevronLeft, ChevronRight, Filter, RefreshCw, Loader2,
  CheckCircle, XCircle, Clock, AlertTriangle, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

const TenantDirectoryPage = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showActions, setShowActions] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const pageSize = 20;

  useEffect(() => {
    loadTenants();
  }, [page, statusFilter]);

  const loadTenants = async () => {
    setLoading(true);
    try {
      const response = await api.listTenants({
        skip: (page - 1) * pageSize,
        limit: pageSize,
        status: statusFilter !== 'all' ? statusFilter : undefined
      });
      
      const data = response.data;
      setTenants(data.tenants || []);
      setTotalCount(data.total || 0);
      setTotalPages(Math.ceil((data.total || 0) / pageSize));
    } catch (err) {
      console.error('Failed to load tenants:', err);
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const filteredTenants = tenants.filter(tenant => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      tenant.name?.toLowerCase().includes(query) ||
      tenant.slug?.toLowerCase().includes(query)
    );
  });

  const getStatusBadge = (tenant) => {
    if (!tenant.is_active) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
          <XCircle className="w-3 h-3" />
          Suspended
        </span>
      );
    }
    if (tenant.subscription?.status === 'trial') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
          <Clock className="w-3 h-3" />
          Trial
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
        <CheckCircle className="w-3 h-3" />
        Active
      </span>
    );
  };

  const handleSuspendTenant = async (tenant) => {
    if (!window.confirm(`Are you sure you want to ${tenant.is_active ? 'suspend' : 'activate'} "${tenant.name}"?`)) {
      return;
    }
    
    setActionLoading(true);
    try {
      await api.updateTenant(tenant._id || tenant.id, {
        is_active: !tenant.is_active
      });
      toast.success(`Tenant ${tenant.is_active ? 'suspended' : 'activated'} successfully`);
      loadTenants();
    } catch (err) {
      toast.error('Failed to update tenant status');
    } finally {
      setActionLoading(false);
      setShowActions(null);
    }
  };

  const handleDeleteTenant = async (tenant) => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete "${tenant.name}" and all its data. This action cannot be undone. Type "DELETE" to confirm.`)) {
      return;
    }
    
    const confirmation = window.prompt('Type "DELETE" to confirm:');
    if (confirmation !== 'DELETE') {
      toast.error('Deletion cancelled');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.deleteTenant(tenant._id || tenant.id);
      toast.success('Tenant deleted successfully');
      loadTenants();
    } catch (err) {
      toast.error('Failed to delete tenant');
    } finally {
      setActionLoading(false);
      setShowActions(null);
    }
  };

  const handleOpenTenant = (tenant) => {
    // Store the tenant context for switching
    localStorage.setItem('impersonated_tenant_id', tenant._id || tenant.id);
    localStorage.setItem('impersonated_tenant_name', tenant.name);
    // Dispatch event to notify the TenantContextIndicator
    window.dispatchEvent(new CustomEvent('tenantContextChanged', { 
      detail: { tenantId: tenant._id || tenant.id, tenantName: tenant.name }
    }));
    toast.success(`Entered tenant context: ${tenant.name}`, {
      description: 'You are now viewing the platform as this tenant. Exit from the top bar.'
    });
    navigate('/dashboard');
  };

  const getEnabledModulesCount = (tenant) => {
    const features = tenant.features || tenant.product_config?.features || {};
    return Object.values(features).filter(v => v === true).length;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tenant Directory</h1>
              <p className="text-sm text-gray-500">Manage all tenants in the platform</p>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or key..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  data-testid="tenant-search-input"
                />
              </div>
              
              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="tenant-status-filter"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="trial">Trial</option>
                </select>
              </div>
              
              {/* Refresh */}
              <button
                onClick={loadTenants}
                disabled={loading}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                data-testid="refresh-tenants-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            
            {/* Create Tenant */}
            <button
              onClick={() => navigate('/platform/tenants/create')}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              data-testid="create-tenant-btn"
            >
              <Plus className="w-4 h-4" />
              Create Tenant
            </button>
          </div>
          
          {/* Stats Row */}
          <div className="flex items-center gap-6 mt-4 pt-4 border-t border-gray-100">
            <div className="text-sm">
              <span className="text-gray-500">Total Tenants:</span>
              <span className="ml-1 font-semibold text-gray-900">{totalCount}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Active:</span>
              <span className="ml-1 font-semibold text-green-600">
                {tenants.filter(t => t.is_active).length}
              </span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Suspended:</span>
              <span className="ml-1 font-semibold text-red-600">
                {tenants.filter(t => !t.is_active).length}
              </span>
            </div>
          </div>
        </div>

        {/* Tenants Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          ) : filteredTenants.length === 0 ? (
            <div className="text-center py-20">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No tenants found</p>
              {searchQuery && (
                <p className="text-sm text-gray-400 mt-1">Try adjusting your search</p>
              )}
            </div>
          ) : (
            <table className="w-full" data-testid="tenants-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Tenant</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Key</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Modules</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Users</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Created</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Last Active</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTenants.map((tenant) => (
                  <tr 
                    key={tenant._id || tenant.id} 
                    className="hover:bg-gray-50 transition-colors"
                    data-testid={`tenant-row-${tenant._id || tenant.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{tenant.name}</p>
                          <p className="text-xs text-gray-500">{tenant.country || 'N/A'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="px-2 py-1 text-xs bg-gray-100 rounded font-mono text-gray-600">
                        {tenant.slug || tenant._id?.slice(-8)}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(tenant)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        <Shield className="w-3.5 h-3.5 text-gray-400" />
                        {getEnabledModulesCount(tenant)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        <Users className="w-3.5 h-3.5 text-gray-400" />
                        {tenant.user_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {tenant.last_active ? new Date(tenant.last_active).toLocaleDateString() : 'Never'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* Enter Tenant Context - switches to viewing as this tenant */}
                        <button
                          onClick={() => handleOpenTenant(tenant)}
                          className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                          title="Enter Tenant Context (View as this tenant)"
                          data-testid={`open-tenant-${tenant._id || tenant.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {/* Edit navigates to tenant config - Module Licensing as primary config */}
                        <button
                          onClick={() => navigate(`/platform/tenants/${tenant._id || tenant.id}/modules`)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Configure Tenant (Modules, Auth, WBS)"
                          data-testid={`edit-tenant-${tenant._id || tenant.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <div className="relative">
                          <button
                            onClick={() => setShowActions(showActions === tenant._id ? null : tenant._id)}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                            title="More actions"
                            data-testid={`more-actions-${tenant._id || tenant.id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          
                          {showActions === (tenant._id || tenant.id) && (
                            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                              <button
                                onClick={() => navigate(`/platform/tenants/${tenant._id || tenant.id}/modules`)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <Shield className="w-4 h-4" />
                                Module Licensing
                              </button>
                              <button
                                onClick={() => navigate(`/platform/tenants/${tenant._id || tenant.id}/auth`)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <Shield className="w-4 h-4" />
                                SSO & Auth Settings
                              </button>
                              <button
                                onClick={() => navigate(`/platform/tenants/${tenant._id || tenant.id}/wbs-schema`)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <FolderKanban className="w-4 h-4" />
                                WBS Schema
                              </button>
                              <button
                                onClick={() => navigate(`/platform/tenants/create?clone=${tenant._id || tenant.id}`)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-indigo-700 hover:bg-indigo-50"
                              >
                                <Copy className="w-4 h-4" />
                                Clone Tenant
                              </button>
                              <hr className="my-1 border-gray-100" />
                              <button
                                onClick={() => handleSuspendTenant(tenant)}
                                disabled={actionLoading}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${
                                  tenant.is_active 
                                    ? 'text-yellow-700 hover:bg-yellow-50' 
                                    : 'text-green-700 hover:bg-green-50'
                                }`}
                              >
                                {tenant.is_active ? (
                                  <>
                                    <Pause className="w-4 h-4" />
                                    Suspend Tenant
                                  </>
                                ) : (
                                  <>
                                    <Play className="w-4 h-4" />
                                    Activate Tenant
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteTenant(tenant)}
                                disabled={actionLoading}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete Tenant
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-500">
                Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} tenants
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Click outside to close actions menu */}
      {showActions && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowActions(null)}
        />
      )}
    </div>
  );
};

export default TenantDirectoryPage;
